import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { ChipConnector } from '~/components/AiSearchChips/ChipConnector/ChipConnector';
import { FilterChip } from '~/components/AiSearchChips/FilterChip/FilterChip';
import { editableValue, isEmptyPredicate, isExcludeMode, isRangeModeOnly, parseNumbers, predicateFromNumbers } from '~/components/AiSearchChips/intRangeChip.utils';
import { CREDIT_CARD_FEES_PAID_BY } from '~/types/api/search-common';
import { ccFeesHasValues, withNetTerms } from './settings-predicates';
import { creditCardFeesSelection, netTermsValueLabel, removeAgreementField, removePaymentField, residualAgreementDescriptors, setRequirePaymentMethod, termsExistenceLabel, termsNameFromInput } from './SettingsChips.utils';
export const GetSettingsChipsTID = (parentTestId)=>getTestIds(parentTestId);
export const SettingsChips = (param)=>{
    let { testId, grammar, paymentSettings, agreementSettings, onPaymentSettingsChange, onAgreementSettingsChange } = param;
    const { SettingsChipsTestIds } = useMemo(()=>GetSettingsChipsTID(testId), [
        testId
    ]);
    const { fields, tone, connector } = grammar;
    const units = [];
    const residualChip = (descriptor)=>/*#__PURE__*/ _jsx(FilterChip, {
            testId: SettingsChipsTestIds.Chip(descriptor.field),
            fieldLabel: descriptor.fieldLabel,
            valueLabel: descriptor.valueLabel,
            onRemove: descriptor.onRemove,
            removeBiName: descriptor.removeBiName,
            tone: tone
        });
    if (paymentSettings != null) {
        const settings = paymentSettings;
        const netTermsField = fields.netTerms;
        const netTerms = settings.netTerms;
        if (netTermsField != null && netTerms != null) {
            const applyNetTerms = (input)=>{
                var _parseNumbers;
                return onPaymentSettingsChange(withNetTerms(settings, predicateFromNumbers((_parseNumbers = parseNumbers(input)) !== null && _parseNumbers !== void 0 ? _parseNumbers : [], isExcludeMode(netTerms))));
            };
            units.push({
                field: 'netTerms',
                node: /*#__PURE__*/ _jsx(AiFilterChip, {
                    mode: "numberRange",
                    testId: SettingsChipsTestIds.Chip('netTerms'),
                    fieldLabel: isEmptyPredicate(netTerms) ? '' : netTermsField.label,
                    value: editableValue(netTerms),
                    valueLabel: netTermsValueLabel(netTerms, netTermsField, connector.labels.or),
                    suffix: netTermsField.suffix,
                    tone: tone,
                    editable: !isRangeModeOnly(netTerms),
                    validate: (input)=>parseNumbers(input) != null,
                    onApply: applyNetTerms,
                    onRemove: ()=>onPaymentSettingsChange(withNetTerms(settings, undefined)),
                    biPrefix: netTermsField.bi,
                    texts: {
                        apply: grammar.apply,
                        validationError: netTermsField.validationError
                    }
                })
            });
        }
        const requirePaymentMethodField = fields.requirePaymentMethod;
        if (requirePaymentMethodField != null && settings.requirePaymentMethod != null) {
            const required = settings.requirePaymentMethod;
            units.push({
                field: 'requirePaymentMethod',
                node: /*#__PURE__*/ _jsx(AiFilterChip, {
                    mode: "toggle",
                    testId: SettingsChipsTestIds.Chip('requirePaymentMethod'),
                    fieldLabel: requirePaymentMethodField.label,
                    valueLabel: required ? requirePaymentMethodField.required : requirePaymentMethodField.notRequired,
                    tone: tone,
                    onToggle: ()=>onPaymentSettingsChange(setRequirePaymentMethod(settings, !required)),
                    onRemove: ()=>onPaymentSettingsChange(removePaymentField(settings, 'requirePaymentMethod')),
                    biPrefix: requirePaymentMethodField.bi
                })
            });
        }
        const ccFeesField = fields.creditCardFeesPaidBy;
        const ccFees = settings.creditCardFeesPaidBy;
        if (ccFeesField != null && ccFees != null && ccFeesHasValues(ccFees)) {
            var _ccFees_in;
            const ccFeesItems = CREDIT_CARD_FEES_PAID_BY.map((value)=>({
                    value,
                    label: value === 'client' ? ccFeesField.paidByClient : ccFeesField.paidByServiceProvider
                }));
            const selected = ccFees.nin != null ? ccFees.nin : (_ccFees_in = ccFees.in) !== null && _ccFees_in !== void 0 ? _ccFees_in : [];
            const operator = ccFees.nin != null ? 'exclude' : 'include';
            units.push({
                field: 'creditCardFeesPaidBy',
                node: /*#__PURE__*/ _jsx(AiFilterChip, {
                    mode: "checkboxes",
                    testId: SettingsChipsTestIds.Chip('creditCardFeesPaidBy'),
                    fieldLabel: ccFeesField.label,
                    items: ccFeesItems,
                    selected: selected,
                    operator: operator,
                    tone: tone,
                    onApply: (next)=>onPaymentSettingsChange(creditCardFeesSelection({
                            settings,
                            next
                        })),
                    onRemove: ()=>onPaymentSettingsChange(removePaymentField(settings, 'creditCardFeesPaidBy')),
                    biPrefix: ccFeesField.bi
                })
            });
        }
    }
    if (agreementSettings != null) {
        const settings = agreementSettings;
        residualAgreementDescriptors(settings, onAgreementSettingsChange, grammar).forEach((descriptor)=>units.push({
                field: descriptor.field,
                node: residualChip(descriptor)
            }));
        const termsField = fields.termsOfService;
        const terms = settings.termsOfService;
        if (termsField != null && terms != null) {
            var _terms_name;
            const termsNameField = termsField.name;
            const termsName = (_terms_name = terms.name) === null || _terms_name === void 0 ? void 0 : _terms_name.contains;
            if (termsNameField != null && termsName != null) {
                units.push({
                    field: 'termsOfService',
                    node: /*#__PURE__*/ _jsx(AiFilterChip, {
                        mode: "text",
                        testId: SettingsChipsTestIds.Chip('termsOfService'),
                        fieldLabel: termsNameField.label,
                        value: termsName,
                        valueLabel: termsNameField.contains(termsName),
                        tone: tone,
                        onApply: (input)=>onAgreementSettingsChange(termsNameFromInput(settings, input)),
                        onRemove: ()=>onAgreementSettingsChange(removeAgreementField(settings, 'termsOfService')),
                        biPrefix: termsNameField.bi,
                        texts: {
                            apply: grammar.apply
                        }
                    })
                });
            } else {
                units.push({
                    field: 'termsOfService',
                    node: /*#__PURE__*/ _jsx(FilterChip, {
                        testId: SettingsChipsTestIds.Chip('termsOfService'),
                        fieldLabel: "",
                        valueLabel: termsExistenceLabel(terms, termsField),
                        onRemove: ()=>onAgreementSettingsChange(removeAgreementField(settings, 'termsOfService')),
                        removeBiName: termsField.bi,
                        tone: tone
                    })
                });
            }
        }
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: units.map((unit, index)=>/*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    index > 0 && /*#__PURE__*/ _jsx(ChipConnector, _object_spread_props(_object_spread({}, connector), {
                        testId: SettingsChipsTestIds.AndConnector(index - 1),
                        tone: "and"
                    })),
                    unit.node
                ]
            }, unit.field))
    });
};
